import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'
import {remoteForm, type ErrorWithResponse} from '@github-ui/remote-form'

const submittingOrganizations = new WeakMap<HTMLFormElement, () => void>()

on(
  'submit',
  'form[data-organization-creation]',
  event => {
    if (submittingOrganizations.has(event.currentTarget as HTMLFormElement)) event.preventDefault()
  },
  {capture: true},
)

window.addEventListener('pageshow', event => {
  if (!event.persisted) return

  for (const form of document.querySelectorAll<HTMLFormElement>('form[data-organization-creation]')) {
    submittingOrganizations.get(form)?.()
  }
})

remoteForm('form[data-organization-creation]', async (form, wants) => {
  const container = form.closest('[data-organization-creation-container]')
  const content = container?.querySelector<HTMLElement>('[data-organization-creation-form]')
  const waiting = container?.querySelector<HTMLElement>('[data-organization-creation-waiting]')
  const errorMessage = container?.querySelector<HTMLElement>('[data-organization-creation-error]')
  if (!container || !content || !waiting || !errorMessage) {
    throw new Error('Missing organization creation markup')
  }

  let showingRecovery = false
  const showRecovery = () => {
    showingRecovery = true
    waiting.hidden = true
    errorMessage.hidden = false
    // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- Reveal the recovery state before focusing it.
    errorMessage.focus()
  }
  submittingOrganizations.set(form, showRecovery)
  content.hidden = true
  waiting.hidden = false
  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write -- Move focus off the hidden form.
  waiting.focus()

  try {
    const response = await wants.json()
    if (!container.isConnected || showingRecovery) return

    const redirectUrl: unknown = response.json.redirect_url
    if (typeof redirectUrl !== 'string' || !redirectUrl) throw new Error('Missing organization creation redirect')
    const destination = new URL(redirectUrl, window.location.href)
    if (destination.origin !== window.location.origin) throw new Error('Invalid organization creation redirect')
    window.location.assign(destination.href)
  } catch (error) {
    if (!container.isConnected || showingRecovery) return

    if (isErrorWithResponse(error) && error.response.status === 422) {
      const replacement = error.response.html.querySelector<HTMLElement>('[data-organization-creation-container]')
      if (replacement) {
        container.replaceWith(replacement)
        const focusTarget =
          replacement.querySelector<HTMLElement>('.flash-error') ??
          replacement.querySelector<HTMLElement>('[aria-invalid="true"]') ??
          replacement.querySelector<HTMLElement>('h1, h2')
        if (focusTarget) {
          if (focusTarget.tabIndex < 0) focusTarget.tabIndex = -1
          focusTarget.focus()
        }
        return
      }
    }

    // A failed response does not prove that creation failed. Keep the form unavailable instead of resubmitting.
    showRecovery()
  }
})

function isErrorWithResponse(error: unknown): error is ErrorWithResponse {
  return error instanceof Error && 'response' in error
}

on('auto-check-success', '.js-new-organization-name', function (event) {
  const field = event.target as HTMLInputElement
  for (const trialLink of document.querySelectorAll<HTMLAnchorElement>('.js-enterprise-trial-link')) {
    updateTrialLink(trialLink, field.value)
  }
})

on('auto-check-success', '.js-new-organization-name', function (event) {
  const field = event.target as HTMLInputElement
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = field.closest<HTMLElement>('dd')!
  const hint = container.querySelector('.js-field-hint-name')
  if (!hint) {
    return
  }
  hint.textContent = field.value
})

observe('.js-hide-org-name-hint', function (event) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const warning = event.parentElement!
  if (warning) {
    warning.hidden = true
  }
})

function updateTrialLink(trialLink: HTMLAnchorElement, orgName: string) {
  const trialURL = new URL(trialLink.href, window.location.origin)
  const searchParams = new URLSearchParams(trialURL.search)

  searchParams.delete('organization_name')
  searchParams.delete('login')
  searchParams.append('organization_name', orgName)
  searchParams.append('login', getLogin())

  const newUrl = new URL(trialLink.href, window.location.origin)
  newUrl.search = searchParams.toString()

  trialLink.href = newUrl.toString()
}

function getLogin(): string {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const loginField = document.querySelector<HTMLInputElement>('#organization_login')!
  return loginField.value
}

// Remove an added member and canceling invitations
remoteForm('.js-org-list-item .js-org-remove-item', async function (form, wants) {
  /* eslint-disable-next-line github/no-d-none, @typescript-eslint/no-non-null-assertion */
  form.closest<HTMLElement>('.js-org-list-item')!.classList.add('d-none')
  try {
    await wants.text()
  } catch {
    /* eslint-disable-next-line github/no-d-none, @typescript-eslint/no-non-null-assertion */
    form.closest<HTMLElement>('.js-org-list-item')!.classList.remove('d-none')
    const errorMessage = form.getAttribute('data-error-message')
    if (errorMessage) {
      alert(errorMessage)
      return
    }
  }

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  form.closest<HTMLElement>('.js-org-list-item')!.remove()
})

on('click', '.js-plan-choice', event => {
  const signupContinueActions = document.querySelector('.SignUpContinueActions')
  const targetValue = (event.target as HTMLInputElement).value

  showCorrectPlanDetails(targetValue)

  if (signupContinueActions != null) {
    signupContinueActions.classList.remove('has-removed-contents')
  }

  const choiceIsDisabled = !(targetValue && targetValue === 'business_plus')
  for (const choice of document.querySelectorAll<HTMLInputElement>('.js-enterprise-choice')) {
    choice.disabled = choiceIsDisabled
  }
})

on('click', '.js-enterprise-choice', event => {
  const billingSection = document.querySelector('.js-billing-section')
  const signupContinueActions = document.querySelector('.SignUpContinueActions')
  const contactUsButton = document.querySelector('.js-enterprise-contact')
  const startTrialButton = document.querySelector('.js-enterprise-trial-link')
  const targetValue = (event.target as HTMLInputElement).value

  const billingElements = [billingSection, signupContinueActions]

  if (contactUsButton) {
    contactUsButton.classList.toggle('has-removed-contents', targetValue !== 'server')
  }
  if (startTrialButton) {
    startTrialButton.classList.toggle('has-removed-contents', targetValue === 'server')
  }

  if (targetValue === 'cloud-trial') {
    if (billingSection) {
      billingSection.classList.add('has-removed-contents')
    }
  } else {
    for (const el of billingElements) {
      if (el != null) {
        el.classList.toggle('has-removed-contents', targetValue !== 'cloud')
      }
    }
  }

  showCorrectPlanDetails('business_plus')
})

function showCorrectPlanDetails(chosenPlan: string) {
  for (const cell of document.querySelectorAll<HTMLElement>('.js-plan-info')) {
    const planClass = classNameForPlan(chosenPlan)
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    if (cell.classList.contains(planClass!)) {
      cell.classList.remove('has-removed-contents')
      cell.hidden = false
    } else {
      cell.classList.add('has-removed-contents')
      cell.hidden = true
    }
  }
}

function classNameForPlan(plan: string): string | null {
  if (plan === 'business_plus') {
    return classNameForEnterprisePlan()
  } else if (plan === 'free') {
    return 'js-free-plan-info'
  } else if (plan === 'business') {
    return 'js-business-plan-info'
  }
  return null
}

function classNameForEnterprisePlan(): string {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const enterpriseType = document.querySelector<HTMLInputElement>('.js-enterprise-choice:checked')!
  if (enterpriseType.value === 'server') {
    return 'js-enterprise-server-plan-info'
  } else if (enterpriseType.value === 'cloud-trial') {
    return 'js-enterprise-cloud-trial-plan-info'
  } else {
    return 'js-enterprise-cloud-plan-info'
  }
}

// New Redesign

// Show "Please specify your industry" custom input on selection of "Other"
on('change', '.js-enterprise-trial-industry', function (event) {
  const target = event.target as HTMLSelectElement

  const selectedIndustry = target.value

  const otherIndustryField = document.querySelector<HTMLElement>('.js-enterprise-trial-other-industry-field')
  if (otherIndustryField) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    otherIndustryField.hidden = selectedIndustry !== otherIndustryField.getAttribute('data-trigger-option-value')!
  }
})

on(
  'details-menu-selected',
  '.js-org-selector',
  function (event) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const summary = document.querySelector('#details-summary')!
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const orgNameField = document.querySelector<HTMLInputElement>('#org-name-input')!
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const orgInput = document.querySelector<HTMLInputElement>('#organization_profile_name')!
    const selected = event.detail.relatedTarget
    const selectedData = selected.getAttribute('data-context-org')
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const orgExistInput = document.querySelector<HTMLInputElement>('#org-exists-input')!

    // eslint-disable-next-line @github-ui/github-monorepo/require-set-sanitized-inner-html -- pre-existing innerHTML sink grandfathered when enabling the rule; not migrated in this rollout
    summary.innerHTML = selected.innerHTML
    if (selectedData) {
      orgInput.setAttribute('value', selectedData)
      orgNameField.hidden = true
      orgExistInput.setAttribute('value', 'true')
    } else {
      orgInput.removeAttribute('value')
      orgNameField.hidden = false
      orgExistInput.setAttribute('value', 'false')
    }
  },
  {capture: true},
)
